"use client";

import React, { useState, useEffect, useCallback, useMemo, startTransition } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { ArrowLeft, FileText, Save, MessageSquare } from "lucide-react";
import { DocumentManager } from "../planning/DocumentManager";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { getCompanyName } from "@/lib/textron-data-processor";
import { ReviewComment } from "../shared/ReviewComment";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface IAUnderDevelopmentProps {
  onBack?: () => void;
}

interface CriteriaRow {
  reference: string;
  description: string;
  remarks: string;
}

export default function IAUnderDevelopment({ onBack }: IAUnderDevelopmentProps) {
  const [question1TextResponse, setQuestion1TextResponse] = useState<any>(null);
  const [question1Documents, setQuestion1Documents] = useState<any[]>([]);
  
  const [criteriaRows, setCriteriaRows] = useState<CriteriaRow[]>([
    {
      reference: "AS 26:44(a)",
      description: "Technical feasibility of completing the intangible asset so that it will be available for use or sale",
      remarks: ""
    },
    {
      reference: "AS 26:44(b)",
      description: "Intention to complete the intangible asset and use or sell it",
      remarks: ""
    },
    {
      reference: "AS 26:44(c)",
      description: "Ability to use or sell the intangible asset",
      remarks: ""
    },
    {
      reference: "AS 26:44(d)",
      description: "How the intangible asset will generate probable future economic benefits",
      remarks: ""
    },
    {
      reference: "AS 26:44(e)",
      description: "Availability of adequate technical, financial, and other resources to complete the development and to use or sell the intangible asset",
      remarks: ""
    },
    {
      reference: "AS 26:44(f)",
      description: "Ability to measure the expenditure attributable to the intangible asset during its development reliably",
      remarks: ""
    },
    {
      reference: "AS 26:58",
      description: "Expenditure on an intangible item that was initially recognized as an expense by a reporting enterprise in previous annual financial statements or interim financial reports should not be recognized as part of the cost of an intangible asset at a later date.",
      remarks: ""
    }
  ]);
  
  const [question2TextResponse, setQuestion2TextResponse] = useState<any>(null);
  const [question2Documents, setQuestion2Documents] = useState<any[]>([]);
  
  const [question3TextResponse, setQuestion3TextResponse] = useState<any>(null);
  const [question3Documents, setQuestion3Documents] = useState<any[]>([]);
  
  const [question4TextResponse, setQuestion4TextResponse] = useState<any>(null);
  const [question4Documents, setQuestion4Documents] = useState<any[]>([]);
  
  const [companyName, setCompanyName] = useState<string>("");
  const [areaPrefix, setAreaPrefix] = useState<string>("IT"); // Default prefix
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasExistingFile, setHasExistingFile] = useState(false);
  const [dirtyQuestions, setDirtyQuestions] = useState<Set<string>>(() => new Set());
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `IA_UNDER_DEVELOPMENT_${normalizedSection}`;
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn("[IAUnderDevelopment] Failed to load company name, using fallback");
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load area prefix from Sections.json
  useEffect(() => {
    const loadAreaPrefix = async () => {
      try {
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const result = await (window as any).sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data) {
            const sectionsData = result.data;
            // Look for "Intangible Assets" in Execution section
            if (sectionsData.Execution && sectionsData.Execution["Intangible Assets"]) {
              setAreaPrefix(sectionsData.Execution["Intangible Assets"]);
            }
          }
        }
      } catch (error) {
        console.warn("[IAUnderDevelopment] Failed to load area prefix from Sections.json, using default IT:", error);
      }
    };
    loadAreaPrefix();
  }, []);

  // Load data from SharePoint
  useEffect(() => {
    const loadData = async () => {
      if (!companyName) {
        return;
      }

      try {
        const fileName = 'Execution_IT_Responses.json';
        
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson(fileName);

          if (result?.success && result?.data?.substantiveProcedures) {
            const procedures = result.data.substantiveProcedures;

            // Load Question 1 (IAUD-1)
            if (procedures["IAUD-1"]) {
              const data = procedures["IAUD-1"];
              if (data.textResponse) {
                setQuestion1TextResponse(data.textResponse);
                // Save to localStorage for DocumentManager
                const htmlContent = data.textResponse.html || data.textResponse.plainText || '';
                if (htmlContent) {
                  localStorage.setItem('textbox_IAUD-1', htmlContent);
                }
              }
              if (data.documents) {
                const formattedDocuments = Array.isArray(data.documents) ? data.documents.map((doc: string | any) => {
                  if (typeof doc === 'string') {
                    return {
                      id: `doc_${doc}`,
                      type: 'linked' as const,
                      name: doc
                    };
                  }
                  return {
                    id: doc.id || `doc_${doc.name || doc}`,
                    type: doc.type || 'linked',
                    name: doc.name || doc,
                    reference: doc.reference,
                    url: doc.url
                  };
                }) : [];
                setQuestion1Documents(formattedDocuments);
              }
            }

            // Load Question 2 (IAUD-2) - includes criteriaRows table
            if (procedures["IAUD-2"]) {
              const data = procedures["IAUD-2"];
              if (data.textResponse) {
                setQuestion2TextResponse(data.textResponse);
                const htmlContent = data.textResponse.html || data.textResponse.plainText || '';
                if (htmlContent) {
                  localStorage.setItem('textbox_IAUD-2', htmlContent);
                }
              }
              if (data.documents) {
                const formattedDocuments = Array.isArray(data.documents) ? data.documents.map((doc: string | any) => {
                  if (typeof doc === 'string') {
                    return {
                      id: `doc_${doc}`,
                      type: 'linked' as const,
                      name: doc
                    };
                  }
                  return {
                    id: doc.id || `doc_${doc.name || doc}`,
                    type: doc.type || 'linked',
                    name: doc.name || doc,
                    reference: doc.reference,
                    url: doc.url
                  };
                }) : [];
                setQuestion2Documents(formattedDocuments);
              }
              // Load criteriaRows table
              if (data.table && Array.isArray(data.table)) {
                // Update existing criteriaRows with saved remarks
                setCriteriaRows(prevRows => {
                  const updatedRows = [...prevRows];
                  data.table.forEach((savedRow: any, index: number) => {
                    if (index < updatedRows.length && savedRow.remarks !== undefined) {
                      updatedRows[index].remarks = savedRow.remarks || "";
                    }
                  });
                  return updatedRows;
                });
              }
            }

            // Load Question 3 (IAUD-3)
            if (procedures["IAUD-3"]) {
              const data = procedures["IAUD-3"];
              if (data.textResponse) {
                setQuestion3TextResponse(data.textResponse);
                const htmlContent = data.textResponse.html || data.textResponse.plainText || '';
                if (htmlContent) {
                  localStorage.setItem('textbox_IAUD-3', htmlContent);
                }
              }
              if (data.documents) {
                const formattedDocuments = Array.isArray(data.documents) ? data.documents.map((doc: string | any) => {
                  if (typeof doc === 'string') {
                    return {
                      id: `doc_${doc}`,
                      type: 'linked' as const,
                      name: doc
                    };
                  }
                  return {
                    id: doc.id || `doc_${doc.name || doc}`,
                    type: doc.type || 'linked',
                    name: doc.name || doc,
                    reference: doc.reference,
                    url: doc.url
                  };
                }) : [];
                setQuestion3Documents(formattedDocuments);
              }
            }

            // Load Question 4 (IAUD-4)
            if (procedures["IAUD-4"]) {
              const data = procedures["IAUD-4"];
              if (data.textResponse) {
                setQuestion4TextResponse(data.textResponse);
                const htmlContent = data.textResponse.html || data.textResponse.plainText || '';
                if (htmlContent) {
                  localStorage.setItem('textbox_IAUD-4', htmlContent);
                }
              }
              if (data.documents) {
                const formattedDocuments = Array.isArray(data.documents) ? data.documents.map((doc: string | any) => {
                  if (typeof doc === 'string') {
                    return {
                      id: `doc_${doc}`,
                      type: 'linked' as const,
                      name: doc
                    };
                  }
                  return {
                    id: doc.id || `doc_${doc.name || doc}`,
                    type: doc.type || 'linked',
                    name: doc.name || doc,
                    reference: doc.reference,
                    url: doc.url
                  };
                }) : [];
                setQuestion4Documents(formattedDocuments);
              }
            }

            setHasExistingFile(true);
            console.log('[IAUnderDevelopment] Loaded data from SharePoint:', fileName);
          } else {
            console.log('[IAUnderDevelopment] No data found in SharePoint (this is normal for first-time use)');
          }
        } else {
          console.warn('[IAUnderDevelopment] sharePointAPI.downloadClientJson is not available');
        }
      } catch (error) {
        console.error('[IAUnderDevelopment] Failed to load data from SharePoint:', error);
      }
    };

    loadData();
  }, [companyName]);

  const markDirty = (questionId: string) => {
    setDirtyQuestions((prev) => {
      const next = new Set(prev);
      next.add(questionId);
      return next;
    });
  };

  const handleRemarksChange = (index: number, value: string) => {
    const updatedRows = [...criteriaRows];
    updatedRows[index].remarks = value;
    setCriteriaRows(updatedRows);
    markDirty("IAUD-2");
  };

  const handleQuestion1DocumentsChange = (docs: any[]) => {
    setQuestion1Documents(docs);
    markDirty("IAUD-1");
  };

  const handleQuestion1TextResponseChange = (textResp: any) => {
    setQuestion1TextResponse(textResp);
    markDirty("IAUD-1");
  };

  const handleQuestion2DocumentsChange = (docs: any[]) => {
    setQuestion2Documents(docs);
    markDirty("IAUD-2");
  };

  const handleQuestion2TextResponseChange = (textResp: any) => {
    setQuestion2TextResponse(textResp);
    markDirty("IAUD-2");
  };

  const handleQuestion3DocumentsChange = (docs: any[]) => {
    setQuestion3Documents(docs);
    markDirty("IAUD-3");
  };

  const handleQuestion3TextResponseChange = (textResp: any) => {
    setQuestion3TextResponse(textResp);
    markDirty("IAUD-3");
  };

  const handleQuestion4DocumentsChange = (docs: any[]) => {
    setQuestion4Documents(docs);
    markDirty("IAUD-4");
  };

  const handleQuestion4TextResponseChange = (textResp: any) => {
    setQuestion4TextResponse(textResp);
    markDirty("IAUD-4");
  };

  const handleSave = useCallback(async () => {
    if (!companyName || !areaPrefix) {
      console.warn("[IAUnderDevelopment] Cannot save: company name or area prefix not available");
      return;
    }

    // Auto-save any pending textbox content before processing responses
    const documentManagers = document.querySelectorAll('[data-document-manager]');
    for (const manager of Array.from(documentManagers)) {
      const itemId = manager.getAttribute('data-item-id');
      if (itemId) {
        const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
        if (autoSaveMethod && typeof autoSaveMethod === 'function') {
          autoSaveMethod();
        }
      }
    }

    // Wait for auto-save to complete
    await new Promise(resolve => setTimeout(resolve, 1000));

    setIsSaving(true);
    setSaveError(null);

    try {
      const fileName = 'Execution_IT_Responses.json';
      
      // Load existing file first
      let existingData: any = {};
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result.data) {
          existingData = result.data;
        }
      } catch (error) {
        console.warn(
          `[IAUnderDevelopment] Failed to download existing ${fileName}, starting from empty`,
          error
        );
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Build payload for dirty questions
      const payload: any = {
        substantiveProcedures: {} as Record<string, any>,
      };

      const questionStates = [
        { id: "IAUD-1", textResponse: question1TextResponse, documents: question1Documents, itemId: "IAUD-1" },
        { id: "IAUD-2", textResponse: question2TextResponse, documents: question2Documents, table: criteriaRows, itemId: "IAUD-2" },
        { id: "IAUD-3", textResponse: question3TextResponse, documents: question3Documents, itemId: "IAUD-3" },
        { id: "IAUD-4", textResponse: question4TextResponse, documents: question4Documents, itemId: "IAUD-4" },
      ];

      questionStates.forEach((qState) => {
        if (!dirtyQuestions.has(qState.id)) return;

        const textboxFromLocalStorage = localStorage.getItem(`textbox_${qState.itemId}`);
        const hasTextboxContent = textboxFromLocalStorage && textboxFromLocalStorage.trim().length > 0;

        const fileDocuments = (qState.documents || []).filter((d: any) => d.type !== 'textbox');
        const documentNames = fileDocuments.map((d: any) => d.name || d);

        let textResponse = { html: "", plainText: "" };
        if (hasTextboxContent) {
          textResponse = {
            html: textboxFromLocalStorage,
            plainText: textboxFromLocalStorage.replace(/<[^>]*>/g, '')
          };
        } else if (qState.textResponse) {
          textResponse = qState.textResponse;
        }

        payload.substantiveProcedures[qState.id] = {
          selectedOption: "",
          textResponse: textResponse,
          documents: documentNames,
          table: qState.table || [],
          lastUpdated: new Date().toISOString(),
          companyName: companyName || "",
        };
      });

      // Merge into existing data
      Object.entries(payload.substantiveProcedures).forEach(([qid, value]) => {
        existingData.substantiveProcedures[qid] = value;
      });

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: fileName,
        referenceValue: `Execution IT Substantive Responses`,
        folderName: "juggernaut",
        fyYear: getFY_YEAR(),
        sectionList: [`Execution_IT_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        console.error(
          `[IAUnderDevelopment] Failed to upload ${fileName}:`,
          uploadResult?.error
        );
        throw new Error(
          uploadResult?.error || `Failed to save ${fileName}`
        );
      }

      setHasExistingFile(true);
      setDirtyQuestions(new Set());
      setIsEditMode(false);
    } catch (error: any) {
      console.error(
        `[IAUnderDevelopment] Error while saving Execution_${areaPrefix}_Responses.json:`,
        error
      );
      setSaveError(
        error?.message || "Failed to save Intangible Assets Under Development responses to SharePoint"
      );
    } finally {
      setIsSaving(false);
    }
  }, [companyName, areaPrefix, dirtyQuestions, question1TextResponse, question1Documents, question2TextResponse, question2Documents, question3TextResponse, question3Documents, question4TextResponse, question4Documents, criteriaRows]);

  const saveButtonLabel = useMemo(() => {
    if (isSaving) return "Saving...";
    if (saveError) return "Save (Retry)";
    if (hasExistingFile) return "Save Changes";
    return "Save";
  }, [hasExistingFile, isSaving, saveError]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Intangible Assets Under Development</h2>
          <p className="text-gray-400">Assessment of internally generated intangible assets</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/30 text-white">
        <CardHeader className="sticky top-0 z-10 bg-black/30 backdrop-blur">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Intangible Assets Under Development</CardTitle>
            <div className="flex items-center gap-2">
              {!isEditMode ? (
                <Button
                  onClick={() => {
                    startTransition(() => {
                      setIsEditMode(true);
                    });
                  }}
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Edit
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {saveButtonLabel}
                  </Button>
                  <Button
                    onClick={() => setIsEditMode(false)}
                    variant="outline"
                    size="sm"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Stop Editing
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>

      {/* Question i */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white text-lg">
            i. Has the ET assessed whether an internally generated intangible asset meets the criteria for recognition by classifying the generation of the asset into: (a) a research phase; and (b) a development phase.
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DocumentManager
            sectionKey="it-substantive"
            itemId="IAUD-1"
            itemQuestion="Has the ET assessed whether an internally generated intangible asset meets the criteria for recognition by classifying the generation of the asset into: (a) a research phase; and (b) a development phase."
            itemTextResponse={question1TextResponse}
            itemDocuments={question1Documents}
            onDocumentsChange={handleQuestion1DocumentsChange}
            onTextResponseChange={handleQuestion1TextResponseChange}
            disabled={!isEditMode}
          />
        </CardContent>
      </Card>

      {/* Question ii */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-lg">
              ii. Whether the intangible asset under development meets ALL of the following criteria:
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Question ii"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border border-white/20 rounded-lg w-full">
            <Table className="w-full table-fixed">
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-white/5">
                  <TableHead className="border border-white/20 px-4 py-2 text-left text-white font-semibold text-sm w-[15%]">
                    Reference
                  </TableHead>
                  <TableHead className="border border-white/20 px-4 py-2 text-left text-white font-semibold text-sm w-[35%] whitespace-normal">
                    Description
                  </TableHead>
                  <TableHead className="border border-white/20 px-4 py-2 text-left text-white font-semibold text-sm w-[50%]">
                    Remarks
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {criteriaRows.map((row, index) => (
                  <TableRow key={index} className="border-white/10 hover:bg-white/5">
                    <TableCell className="border border-white/20 px-4 py-2 text-white text-sm">
                      {row.reference}
                    </TableCell>
                    <TableCell className="border border-white/20 px-4 py-2 text-white text-sm whitespace-normal">
                      <div className="break-words word-wrap break-word">
                        {row.description}
                      </div>
                    </TableCell>
                    <TableCell className="border border-white/20 px-4 py-2">
                      <Textarea
                        value={row.remarks}
                        onChange={(e) => handleRemarksChange(index, e.target.value)}
                        disabled={!isEditMode}
                        className="min-h-[60px] w-full border-white/10 bg-black/40 text-white placeholder:text-white/50 focus:border-[#4da3ff]/50 focus:ring-[#4da3ff]/20 resize-none disabled:opacity-50"
                        placeholder="Enter remarks..."
                        rows={2}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4">
            <DocumentManager
              sectionKey="it-substantive"
              itemId="IAUD-2"
              itemQuestion="Whether the intangible asset under development meets ALL of the following criteria"
              itemTextResponse={question2TextResponse}
              itemDocuments={question2Documents}
              onDocumentsChange={handleQuestion2DocumentsChange}
              onTextResponseChange={handleQuestion2TextResponseChange}
              disabled={!isEditMode}
            />
          </div>
        </CardContent>
      </Card>

      {/* Question iii */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-lg">
              iii. Document test of details performed on the additions to intangible assets under development during the current period.
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Question iii"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <DocumentManager
            sectionKey="it-substantive"
            itemId="IAUD-3"
            itemQuestion="Document test of details performed on the additions to intangible assets under development during the current period."
            itemTextResponse={question3TextResponse}
            itemDocuments={question3Documents}
            onDocumentsChange={handleQuestion3DocumentsChange}
            onTextResponseChange={handleQuestion3TextResponseChange}
            disabled={!isEditMode}
          />
        </CardContent>
      </Card>

      {/* Question iv */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-lg">
              iv. Document the impairment assessment of the same in "Impairment Testing" phase.
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Question iv"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <DocumentManager
            sectionKey="it-substantive"
            itemId="IAUD-4"
            itemQuestion="Document the impairment assessment of the same in 'Impairment Testing' phase."
            itemTextResponse={question4TextResponse}
            itemDocuments={question4Documents}
            onDocumentsChange={handleQuestion4DocumentsChange}
            onTextResponseChange={handleQuestion4TextResponseChange}
            disabled={!isEditMode}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Question i")]: "i. Has the ET assessed whether an internally generated intangible asset meets the criteria for recognition by classifying the generation of the asset into: (a) a research phase; and (b) a development phase.",
              [getSectionQuestionId("Question ii")]: "ii. Whether the intangible asset under development meets ALL of the following criteria:",
              [getSectionQuestionId("Question iii")]: "iii. Document test of details performed on the additions to intangible assets under development during the current period.",
              [getSectionQuestionId("Question iv")]: "iv. Document the impairment assessment of the same in \"Impairment Testing\" phase.",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
      </CardContent>
    </Card>
    </div>
  );
}
